/**
 * Cache local do catálogo de produtos (StoreService.products).
 *
 * Objetivo: para lojas com catálogos grandes, evitar que o usuário fique
 * preso no skeleton de carregamento a cada acesso. Na primeira vez o catálogo
 * é buscado na API e salvo no localStorage com um TTL. Nos acessos seguintes
 * o catálogo é servido imediatamente do cache local. Se o cache estiver
 * vencido, servimos o dado "stale" (antigo) na hora e disparamos um fetch em
 * background para atualizar o cache.
 */

//* Tempo de vida do cache: 2 minutos.
export const CATALOG_CACHE_TTL_MS = 2 * 60 * 1000;

//* Prefixo das chaves no localStorage.
const CATALOG_CACHE_PREFIX = 'catalog_cache:';

//* Versão do formato do cache. Alterar invalida caches antigos.
const CATALOG_CACHE_VERSION = 1;

interface CatalogCacheEntry {
    v: number;
    savedAt: number;
    data: any;
}

export interface CatalogCacheResult {
    data: any;
    savedAt: number;
    isExpired: boolean;
}

export interface CatalogCacheKeyParams {
    store_id: number | string;
    order_type: number | string;
    catalog_type?: string;
    lang?: string | null;
}

/**
 * Monta uma chave determinística para o cache, isolando por loja, tipo de
 * pedido, tipo de catálogo e idioma (o catálogo muda quando o idioma muda).
 */
export const buildCatalogCacheKey = (params: CatalogCacheKeyParams): string => {
    const lang = params.lang ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null) ?? '';
    const catalogType = params.catalog_type ?? '';
    return `${CATALOG_CACHE_PREFIX}${params.store_id}:${params.order_type}:${catalogType}:${lang}`;
};

/**
 * Lê o catálogo do cache local. Retorna null quando não existe cache válido.
 * O campo isExpired indica se o TTL já venceu (mas o dado ainda pode ser
 * usado como "stale" enquanto um novo fetch acontece em background).
 */
export const getCatalogCache = (key: string): CatalogCacheResult | null => {
    try {
        if (typeof localStorage === 'undefined') return null;

        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const entry = JSON.parse(raw) as CatalogCacheEntry;

        if (!entry || entry.v !== CATALOG_CACHE_VERSION || typeof entry.savedAt !== 'number') {
            localStorage.removeItem(key);
            return null;
        }

        return {
            data: entry.data,
            savedAt: entry.savedAt,
            isExpired: Date.now() - entry.savedAt > CATALOG_CACHE_TTL_MS,
        };
    } catch (error) {
        //* JSON inválido / storage indisponível: trata como sem cache.
        try {
            localStorage.removeItem(key);
        } catch { /* ignore */ }
        return null;
    }
};

/**
 * Salva o catálogo no cache local com o timestamp atual.
 */
export const setCatalogCache = (key: string, data: any): void => {
    try {
        if (typeof localStorage === 'undefined') return;

        //* Só cacheia respostas com conteúdo útil.
        if (!Array.isArray(data) || data.length === 0) return;

        const entry: CatalogCacheEntry = {
            v: CATALOG_CACHE_VERSION,
            savedAt: Date.now(),
            data,
        };

        localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
        //* Quota excedida ou storage indisponível: falha silenciosa,
        //* o app continua funcionando buscando da rede.
        console.warn('Não foi possível salvar o cache do catálogo:', error);
    }
};

/**
 * Remove uma entrada específica do cache.
 */
export const clearCatalogCache = (key: string): void => {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.removeItem(key);
    } catch { /* ignore */ }
};

/**
 * Remove todas as entradas de cache de catálogo (útil em logout / troca de loja).
 */
export const clearAllCatalogCache = (): void => {
    try {
        if (typeof localStorage === 'undefined') return;
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CATALOG_CACHE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch { /* ignore */ }
};
